import React, { useState } from 'react';
import { useWallet } from '../../hooks/useWallet';
import './WalletAuth.css';

interface WalletAuthProps {
  onAuthSuccess?: () => void;
  onAuthError?: (error: string) => void;
  className?: string;
  showSessionInfo?: boolean;
}

export const WalletAuth: React.FC<WalletAuthProps> = ({
  onAuthSuccess,
  onAuthError,
  className = '',
  showSessionInfo = true,
}) => {
  const wallet = useWallet();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handleConnect = async () => {
    try {
      await wallet.connect();
      // Check authorization immediately after connecting
      if (wallet.address) {
        await wallet.checkWalletAuthorization();
      }
    } catch (error: any) {
      console.error('Connection failed:', error);
      onAuthError?.(error.message || 'Failed to connect wallet');
    }
  };

  const handleAuthenticate = async () => {
    // Check authorization first
    if (wallet.address && wallet.isAuthorized === null) {
      try {
        await wallet.checkWalletAuthorization();
      } catch (error: any) {
        onAuthError?.('Failed to check wallet authorization');
        return;
      }
    }

    // If wallet is not authorized, show error and don't proceed
    if (wallet.isAuthorized === false) {
      onAuthError?.(wallet.authorizationMessage || 'This wallet is not authorized');
      return;
    }

    setIsAuthenticating(true);
    try {
      await wallet.authenticateWallet();
      onAuthSuccess?.();
    } catch (error: any) {
      console.error('Authentication failed:', error);
      onAuthError?.(error.message || 'Failed to authenticate wallet');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleConnectAndAuthenticate = async () => {
    setIsAuthenticating(true);
    try {
      await wallet.connectAndAuthenticate();
      onAuthSuccess?.();
    } catch (error: any) {
      console.error('Connect and authenticate failed:', error);
      onAuthError?.(error.message || 'Failed to connect and authenticate wallet');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleRevokeSession = async () => {
    try {
      await wallet.revokeSession();
    } catch (error: any) {
      console.error('Session revocation failed:', error);
      onAuthError?.(error.message || 'Failed to revoke session');
    }
  };

  const handleDisconnect = () => {
    wallet.disconnect();
  };

  const formatSessionExpiry = () => {
    if (!wallet.sessionExpiresAt) return '';
    
    if (wallet.sessionExpiresInDays >= 1) {
      return `${wallet.sessionExpiresInDays} day${wallet.sessionExpiresInDays !== 1 ? 's' : ''}`;
    } else if (wallet.sessionExpiresInHours >= 1) {
      return `${wallet.sessionExpiresInHours} hour${wallet.sessionExpiresInHours !== 1 ? 's' : ''}`;
    } else {
      const minutes = Math.floor(wallet.sessionExpiresIn / (1000 * 60));
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  // Not connected state
  if (!wallet.isConnected) {
    return (
      <div className={`wallet-auth ${className}`}>
        <div className="wallet-auth-card">
          <h3>Connect Wallet</h3>
          <p>Connect your MetaMask wallet to start trading</p>
          
          {wallet.error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {wallet.error}
            </div>
          )}
          
          <button
            onClick={handleConnect}
            disabled={wallet.isLoading}
            className="connect-button primary"
          >
            {wallet.status === 'connecting' ? (
              <>
                <span className="spinner">⏳</span>
                Connecting...
              </>
            ) : (
              <>
                <span className="wallet-icon">🦊</span>
                Connect MetaMask
              </>
            )}
          </button>
          
          <button
            onClick={handleConnectAndAuthenticate}
            disabled={wallet.isLoading || isAuthenticating}
            className="connect-button secondary"
          >
            {isAuthenticating ? (
              <>
                <span className="spinner">⏳</span>
                Connecting & Authenticating...
              </>
            ) : (
              <>
                <span className="auth-icon">🔐</span>
                Connect & Authenticate
              </>
            )}
          </button>
          
          <p className="auth-note">
            Authentication creates a secure 30-day session for automated trading
          </p>
        </div>
      </div>
    );
  }

  // Connected but not authenticated
  if (wallet.isConnected && !wallet.isAuthenticated) {
    return (
      <div className={`wallet-auth ${className}`}>
        <div className="wallet-auth-card">
          <div className="wallet-status connected">
            <span className="status-icon">✅</span>
            <div>
              <h3>Wallet Connected</h3>
              <p className="wallet-address">{wallet.address}</p>
            </div>
          </div>
          
          {/* Show authorization status */}
          {wallet.isAuthorized === false && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <div>
                <div>{wallet.authorizationMessage || 'This wallet is not authorized for this application'}</div>
                {wallet.authorizationSuggestion && (
                  <div style={{ marginTop: '8px', fontSize: '13px', fontStyle: 'italic' }}>
                    {wallet.authorizationSuggestion}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Show other auth errors only if wallet is authorized or authorization status is unknown */}
          {wallet.isAuthorized !== false && (wallet.authError || wallet.error) && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {wallet.authError || wallet.error}
            </div>
          )}
          
          <div className="auth-section">
            <h4>Authentication Required</h4>
            <p>
              {wallet.isAuthorized === false
                ? 'This wallet cannot access the application'
                : 'Sign a message to create a secure trading session'
              }
            </p>
            
            <button
              onClick={handleAuthenticate}
              disabled={
                wallet.authStatus === 'authenticating' || 
                wallet.isAuthorized === false
              }
              className={`auth-button ${wallet.isAuthorized === false ? 'disabled' : 'primary'}`}
            >
              {wallet.authStatus === 'authenticating' ? (
                <>
                  <span className="spinner">⏳</span>
                  Authenticating...
                </>
              ) : wallet.isAuthorized === false ? (
                <>
                  <span className="error-icon">🚫</span>
                  Wallet Not Authorized
                </>
              ) : (
                <>
                  <span className="auth-icon">🔐</span>
                  Authenticate Wallet
                </>
              )}
            </button>
          </div>
          
          <button
            onClick={handleDisconnect}
            className="disconnect-button"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  // Connected and authenticated
  return (
    <div className={`wallet-auth ${className}`}>
      <div className="wallet-auth-card authenticated">
        <div className="wallet-status authenticated">
          <span className="status-icon">🔐</span>
          <div>
            <h3>Wallet Authenticated</h3>
            <p className="wallet-address">{wallet.address}</p>
          </div>
        </div>
        
        {showSessionInfo && (
          <div className="session-info">
            <div className="session-details">
              <div className="session-status">
                <span className="session-icon">⏰</span>
                <span>Session expires in {formatSessionExpiry()}</span>
              </div>
              {wallet.sessionExpired && (
                <div className="session-expired">
                  <span className="warning-icon">⚠️</span>
                  Session expired - please re-authenticate
                </div>
              )}
            </div>
          </div>
        )}
        
        {(wallet.authError || wallet.error) && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {wallet.authError || wallet.error}
          </div>
        )}
        
        <div className="session-actions">
          <button
            onClick={wallet.checkAuthStatus}
            disabled={wallet.authStatus === 'checking'}
            className="refresh-button"
          >
            {wallet.authStatus === 'checking' ? (
              <>
                <span className="spinner">⏳</span>
                Checking...
              </>
            ) : (
              <>
                <span className="refresh-icon">🔄</span>
                Refresh Session
              </>
            )}
          </button>
          
          <button
            onClick={handleRevokeSession}
            className="revoke-button"
          >
            <span className="revoke-icon">🔓</span>
            Revoke Session
          </button>
          
          <button
            onClick={handleDisconnect}
            className="disconnect-button"
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalletAuth; 